"use client";

import React from "react";
import { CircleCheck as CheckCircle2, Dna, FileCheck, Fingerprint, Network, ShieldAlert, ArrowRight, Sparkles } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
interface ApplicationItem {
  id: string;
  title: string;
  category: string;
  description: string;
  capabilities: string;
  icon: React.ElementType;
  accent: string;
}
const applications: ApplicationItem[] = [{
  id: "auth",
  title: "Ingredient Authentication",
  category: "Raw Material Verification",
  description: "Rapid spectroscopy and fingerprinting to confirm raw ingredient botanical origin, purity, and grade before batch release.",
  capabilities: "NIR and Raman raw material ID, adulteration screening, botanical fingerprint verification",
  icon: Fingerprint,
  accent: "bg-primary/10 text-primary"
}, {
  id: "contam",
  title: "Contamination Detection",
  category: "Safety & Hazard Control",
  description: "Sensitive screening for physical particulates, cross-contact allergens, chemical residues, and unwanted process byproducts.",
  capabilities: "Micro-spectroscopy, residue quantification, cross-contamination threshold monitoring",
  icon: ShieldAlert,
  accent: "bg-destructive/10 text-destructive"
}, {
  id: "trace",
  title: "Supply Chain Traceability",
  category: "Integrity & Chain of Custody",
  description: "End-to-end analytical records correlating supplier batch signatures with in-line manufacturing performance and distribution logs.",
  capabilities: "Digital batch records, origin verification, supplier material comparability studies",
  icon: Network,
  accent: "bg-chart-2/10 text-chart-2"
}, {
  id: "quality",
  title: "Quality Testing",
  category: "In-Process & Final Release",
  description: "Real-time process analytical technology (PAT) to monitor moisture, blend uniformity, fat content, and active constituents.",
  capabilities: "In-line blend monitoring, powder flow characterization, moisture & endpoint detection",
  icon: Dna,
  accent: "bg-chart-3/10 text-chart-3"
}, {
  id: "reg",
  title: "Regulatory Compliance",
  category: "Audit & Validation",
  description: "Full qualification packages, method transfers, and secure data management supporting regulated food production.",
  capabilities: "IQ/OQ/PQ protocols, tamper-evident audit trails, routine verification support",
  icon: FileCheck,
  accent: "bg-chart-4/10 text-chart-4"
}];
export default function FoodApplications() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0
    },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.1,
        delayChildren: 0.1
      }
    }
  };
  const itemVariants = {
    hidden: {
      opacity: shouldReduceMotion ? 1 : 0,
      y: shouldReduceMotion ? 0 : 20
    },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.4,
        ease: "easeOut"
      }
    }
  };
  return <section id="foodindustryapplications" data-nav="light" className="bg-background py-16 md:py-24 border-b border-border transition-colors">
      <div className="page-container">
        {/* Section Header */}
        <div className="max-w-3xl mb-12 md:mb-16">
          <div className="flex items-center gap-2 mb-3">
            <Badge variant="secondary" className="px-3 py-1 text-xs font-semibold uppercase tracking-wider">
              Food & Beverage Analytical Solutions
            </Badge>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold font-default text-foreground tracking-tight mb-4">
            Analytical Applications Built for Food Safety, Quality & Compliance
          </h2>
          <p className="text-base md:text-lg text-muted-foreground font-default leading-relaxed">
            From incoming ingredient verification to in-line process control and audit-ready reporting, Antech Solutions equips food and ingredient manufacturers with validated, regulatory-compliant analytical systems across Ireland and the UK.
          </p>
        </div>

        {/* Applications Grid */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="visible" viewport={{
        once: true,
        margin: "-50px"
      }} className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {applications.map((app, index) => {
          const IconComponent = app.icon;
          return <motion.div key={app.id} data-index={index} variants={itemVariants} className="h-full">
                <Card className="h-full border border-border shadow-xs hover:shadow-md transition-shadow flex flex-col justify-between bg-card text-card-foreground">
                  <div>
                    <CardHeader className="pb-3">
                      <div className="flex items-center justify-between mb-3">
                        <div className={`p-3 rounded-lg ${app.accent} inline-flex items-center justify-center`}>
                          <IconComponent className="size-6" />
                        </div>
                        <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                          {app.category}
                        </span>
                      </div>
                      <CardTitle className="text-xl font-bold font-default text-card-foreground">
                        {app.title}
                      </CardTitle>
                      <CardDescription className="text-muted-foreground text-sm font-default mt-2 leading-normal">
                        {app.description}
                      </CardDescription>
                    </CardHeader>

                    <CardContent className="pt-2">
                      <div className="border-t border-border/60 pt-4 mt-2">
                        <div className="flex items-start gap-2 text-xs text-foreground/80 font-default">
                          <CheckCircle2 className="size-4 text-primary shrink-0 mt-0.5" />
                          <span>{app.capabilities}</span>
                        </div>
                      </div>
                    </CardContent>
                  </div>

                  <div className="px-6 pb-6 pt-2">
                    <Button asChild variant="ghost" size="sm" className="w-full justify-between text-foreground hover:text-primary px-0 font-medium">
                      <Link to="/SolutionsPage">
                        <span>Explore application specs</span>
                        <ArrowRight className="size-4" />
                      </Link>
                    </Button>
                  </div>
                </Card>
              </motion.div>;
        })}

          {/* Quick Highlight Box as 6th Grid Element for Balanced 3x2 Layout */}
          <motion.div data-index={5} variants={itemVariants} className="h-full">
            <div className="h-full bg-secondary text-secondary-foreground rounded-xl p-6 border border-border flex flex-col justify-between">
              <div>
                <div className="inline-flex items-center gap-2 text-primary font-semibold text-xs tracking-wider uppercase mb-3">
                  <Sparkles className="size-4" />
                  Custom Method Support
                </div>
                <h3 className="text-xl font-bold font-default mb-2 text-foreground">
                  Regulated Food Method Development
                </h3>
                <p className="text-sm text-muted-foreground font-default leading-relaxed mb-4">
                  Need qualification protocols or custom calibration models for your specific food matrix? Our team provides on-site validation and lifecycle support.
                </p>
              </div>

              <div className="space-y-3 pt-4 border-t border-border">
                <Button asChild className="w-full justify-center">
                  <Link to="/ContactPage">
                    Speak with a Food Specialist
                  </Link>
                </Button>
                <Button asChild variant="outline" className="w-full justify-center bg-card">
                  <Link to="/ProcessAnalyticalTechnologyPage">
                    View PAT Instrumentation
                  </Link>
                </Button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      </div>
    </section>;
}